<template>
  <v-row class="fill-height">
    <v-col>
      <v-sheet height="64" elevation="1">
        <v-toolbar flat color="white">
          <v-btn fab text small color="grey darken-2" data-cy="prev" @click="prev">
            <v-icon small>chevron_left</v-icon>
          </v-btn>
          <v-btn fab text small color="grey darken-2" data-cy="next" @click="next">
            <v-icon small>chevron_right</v-icon>
          </v-btn>
          <v-toolbar-title class="ml-4">{{ title }}</v-toolbar-title>
          <v-spacer/>
          <v-btn class="mr-4" color="success" @click="setToday">
            {{ $t('move.calendar.today') }}
          </v-btn>
          <v-menu bottom right>
            <template v-slot:activator="{ on }">
              <v-btn
                color="success"
                v-on="on"
                data-cy="change-calendar-type"
              >
                <span>{{ typeToLabel[type] }}</span>
                <v-icon right>arrow_drop_down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="type = 'day'">
                <v-list-item-title>{{$t('move.calendar.day')}}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = 'week'">
                <v-list-item-title>{{$t('move.calendar.week')}}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = 'month'">
                <v-list-item-title>{{$t('move.calendar.month')}}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-toolbar>
      </v-sheet>
      <v-sheet height="calc(100vh - 176px)">
        <v-calendar
          ref="calendar"
          v-model="focus"
          :class="{'v-calendar-weekly' : (type === 'week')}"
          color="primary"
          :events="movesInfo"
          :event-color="getMoveColor"
          :now="today"
          :type="type"
          event-more
          :interval-count="0"
          @click:event="showMovePreview"
          @click:more="showMoreEvents"
          @click:date="viewDay"
          @change="updateRange"
          data-cy="calendar"
        >
          <template v-if="type==='day'" v-slot:day-header="{ date }">
            <template
              class="text-center"
              v-for="item in getEvents(date)"
            >
              <div
                :key="item.moveId"
                @click="showMovePreview({nativeEvent: $event, event: item})"
                class="v-event v-event-start white--text v-custom-event"
                :class="item.color"
                style="height: 20px; width: 95%; margin-bottom: 1px;"
                v-ripple>
                <div class="pl-1">
                  {{item.name}}
                </div>
              </div>
            </template>
          </template>
        </v-calendar>
        <v-menu
          v-model="selectedOpen"
          :close-on-content-click="false"
          :activator="selectedElement"
          offset-y
          :max-width="400"
          bottom
        >
          <v-card
            class="mt-1"
            color="grey lighten-4"
            min-width="300px"
            flat
            data-cy="event-info"
          >
            <v-toolbar
              :color="selectedEvent.color"
              dark
            >
              <v-icon class="mr-2">directions_walk</v-icon>
              <v-toolbar-title v-html="selectedEvent.name"/>
              <v-spacer/>
              <v-btn
                fab
                text
                small
                @click="selectedOpen = false"
              >
                <v-icon data-cy="close-event-info">close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text v-if="selectedEvent.details">
              <div>
                <strong>{{ $t('move.properties.transferee') }}:</strong>
                {{ selectedEvent.details.transfereeFirstName }} {{ selectedEvent.details.transfereeLastName }}
              </div>
              <div>
                <strong>{{ $t('move.properties.startDate') }}:</strong>
                {{ new Date(selectedEvent.details.startDate).toLocaleDateString() }}
              </div>
              <div>
                <strong>{{ $t('move.properties.endDate') }}:</strong>
                {{ new Date(selectedEvent.details.plannedDeliveryDate).toLocaleDateString() }}
              </div>
              <div v-if="selectedEvent.details.origin">
                <strong>{{ $t('move.properties.origin') }}:</strong>
                {{selectedEvent.details.origin.street}} {{selectedEvent.details.origin.number}}<br>
                {{selectedEvent.details.origin.city}}{{selectedEvent.details.origin.state ? ',' : null}}
                {{selectedEvent.details.origin.state}}
              </div>
              <div v-if="selectedEvent.details.destination">
                <strong>{{ $t('move.properties.destination') }}:</strong>
                {{selectedEvent.details.destination.street}} {{selectedEvent.details.destination.number}}<br>
                {{selectedEvent.details.destination.city}}{{selectedEvent.details.destination.state ? ',' : null}}
                {{selectedEvent.details.destination.state}}
              </div>
              <div v-if="selectedEvent.details.device">
                <strong>{{ $t('move.properties.tracker') }}:</strong>
                {{selectedEvent.details.device.name}}
              </div>
              <div>
                <strong>{{ $t('move.properties.status') }}:</strong>
                {{ $t(`move.type.${MOVE_STATUS_TYPE_ARR[selectedEvent.details.moveStatus]}`) }}
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn
                v-if="showTrackingHistoryBtn"
                :style="{color: 'white'}"
                :color="selectedEvent.color"
                @click="showTrackingHistory(selectedEvent.details)"
              >
                <v-icon left small>
                  track_changes
                </v-icon>
                {{$t('move.calendar.showTrackingHistory')}}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
        <v-dialog
          v-model="showMoreOpen"
          :max-width="500"
        >
          <v-card
            class="mt-1"
            color="grey lighten-4"
            flat
            data-cy="show-more-info"
          >
            <v-toolbar>
              <v-toolbar-title>
                {{$t('move.calendar.showMoreDialogTitle', {date: new Date(showMoreDate).toLocaleDateString()})}}
              </v-toolbar-title>
              <v-spacer/>
              <v-btn
                fab
                text
                small
                @click="showMoreOpen = false"
                data-cy="close-more-info"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text class="pt-6">
              <template
                class="text-center"
                v-for="item in getEvents(this.showMoreDate)"
              >
                <div
                  :key="item.moveId"
                  @click="showMovePreview({nativeEvent: $event, event: item})"
                  class="v-event v-event-start white--text v-custom-event"
                  :class="item.color"
                  style="height: 20px;border-radius: 4px;margin-bottom: 1px;font-size: 12px;"
                  v-ripple>
                  <div class="pl-1">
                    {{item.name}}
                  </div>
                </div>
              </template>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-sheet>
    </v-col>
  </v-row>
</template>

<script>
  import {mapActions, mapGetters} from 'vuex';
  import * as routeConstants from '@/router/constants';
  import * as moveEnums from '@/constants/move';
  import {
    MOVE_STATUS_TYPE_ARR,
  } from '@/constants';
  import * as actions from '@/store/actions.type';

  export default {
    name: 'MovesCalendar',
    data: () => ({
      focus: '',
      type: 'month',
      typeToLabel: {
        month: 'Month',
        week: 'Week',
        day: 'Day',
      },
      start: null,
      end: null,
      firstDay: null,
      lastDay: null,
      today: null,
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      showMoreDate: null,
      showMoreOpen: false,
      colors: moveEnums.MOVE_COLOR_BY_STATUS,
      moveEnums: moveEnums,
      routeConstants: routeConstants,
      MOVE_STATUS_TYPE_ARR,
      showTrackingHistoryBtn: true
    }),
    computed: {
      ...mapGetters([
        'moves',
      ]),
      title() {
        const {start, end} = this;
        if (!start || !end) {
          return '';
        }

        const startMonth = this.monthFormatter(start);
        const endMonth = this.monthFormatter(end);
        const suffixMonth = startMonth === endMonth ? '' : endMonth;

        const startYear = start.year;
        const endYear = end.year;
        const suffixYear = startYear === endYear ? '' : endYear;

        const startDay = start.day + this.nth(start.day);
        const endDay = end.day + this.nth(end.day);

        switch (this.type) {
          case 'month':
            return `${startMonth} ${startYear}`;
          case 'week':
            return `${startMonth} ${startDay} ${startYear} - ${suffixMonth} ${endDay} ${suffixYear}`;
          case 'day':
            return `${startMonth} ${startDay} ${startYear}`;
        }

        return '';
      },
      monthFormatter() {
        return this.$refs.calendar.getFormatter({
          timeZone: 'UTC', month: 'long',
        });
      },
      movesInfo() {
        let movesInfo = [];

        this.moves.forEach(move => {
          movesInfo.push({
            name: move.name,
            start: this.formatDate(new Date(move.startDate)),
            end: this.formatDate(new Date(move.plannedDeliveryDate)),
            color: this.colors[move.moveStatus],
            details: move,
          });
        });

        return movesInfo;
      },
      movesParams() {
        return {
          movesFromDate: this.firstDay,
          movesToDate: this.lastDay,
        }
      },
    },
    watch: {
      selectedEvent(value) {
        value.details.device ? this.showTrackingHistoryBtn = true : this.showTrackingHistoryBtn = false;
      }
    },
    methods: {
      ...mapActions({
        fetchMovesAction: actions.FETCH_MOVES,
      }),
      viewDay({date}) {
        this.focus = date;
        this.type = 'day';
      },
      getMoveColor(event) {
        return event.color;
      },
      setToday() {
        this.focus = this.today;
      },
      prev() {
        this.$refs.calendar.prev();
      },
      next() {
        this.$refs.calendar.next();
      },
      showMovePreview({nativeEvent, event}) {
        const open = () => {
          this.selectedEvent = event;
          this.selectedElement = nativeEvent.target;
          setTimeout(() => this.selectedOpen = true, 10);
        };

        if (this.selectedOpen) {
          this.selectedOpen = false;
          setTimeout(open, 10);
        } else {
          open();
        }

        nativeEvent.stopPropagation();
      },
      showMoreEvents({date}) {
        const open = () => {
          this.showMoreDate = date;
          setTimeout(() => this.showMoreOpen = true, 10);
        };

        if (this.showMoreOpen) {
          this.showMoreOpen = false;
          setTimeout(open, 10);
        } else {
          open();
        }
      },
      updateRange({start, end}) {
        this.start = start;
        this.end = end;

        const startDate = new Date(start.date);
        const endDate = new Date(end.date);

        start.weekday === 0
          ? this.firstDay = new Date(start.date)
          : this.firstDay = new Date(startDate.getTime() - (start.weekday * 24 * 60 * 60 * 1000));

        end.weekday === 6
          ? this.lastDay = new Date(end.date)
          : this.lastDay = new Date(endDate.getTime() + ((6 - end.weekday) * 24 * 60 * 60 * 1000));

        this.fetchMovesAction(this.movesParams);
      },
      nth(d) {
        return d > 3 && d < 21
          ? 'th'
          : ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th'][d % 10];
      },
      formatDate(date) {
        return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
      },
      getEvents(date) {
        let eventDate = new Date(date);
        eventDate.setHours(0, 0, 0, 0);

        const filteredMoves = [];

        this.movesInfo.forEach(move => {
          const moveStartDate = new Date(move.start);
          const moveEndDate = new Date(move.end);

          if (eventDate >= moveStartDate && eventDate <= moveEndDate) {
            filteredMoves.push(move);
          }
        });

        return filteredMoves;
      },
      showTrackingHistory(item) {
        if (item.device) {
          this.$router.push({
            path: routeConstants.DEVICE_DETAILS_URL.replace(':id', item.device.id),
            query: {moveId: item.moveId}
          })
        }
      },
    },
  };
</script>

<style scoped>

</style>